import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	useTranslationAdminDashboard,
	useTranslationDefault,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Button, Flex, Text } from '@mantine/core';
import { SecuritiesTable } from './components/SecuritiesTable/SecuritiesTable.tsx';
import {
	CurrencyType,
	GetPersonSecurityDataRes,
	SecurityType,
} from '@dealstack/shared/lib/generated';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { VestingTerms } from '@dealstack/frontend/shared/components/VestingTerms/VestingTerms';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { updateMip as updateMipRequest } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { useParams } from 'react-router-dom';
import { useProgrammeSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useProgrammeSetting';
import { updateProgrammeSetting } from '@dealstack/frontend/shared/dataExchange/services/programme-setting.service';
import { SecurityFormData } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/types.ts';
import { ValuationSettingsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/components/ValuationSettingDrawer/ValuationSettingsDrawer.tsx';
import { EditSecurityDrawer } from '@dealstack/frontend/shared/components/EditSecurityDrawer/EditSecurityDrawer';
import { CreateSecurityDrawer } from '@dealstack/frontend/shared/components/CreateSecurityDrawer/CreateSecurityDrawer';
import {
	CreateSecurityFormData,
	SubmitOptions,
} from '@dealstack/frontend/shared/components/CreateSecurityDrawer/CreateSecurityDrawer.types';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import { useDeleteSecurity } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/hooks/useDeleteSecurity.ts';
import { useUpdateSecurity } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/hooks/useUpdateSecurity.ts';
import { useCreateSecurity } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/hooks/useCreateSecurity.ts';
import { useGetPersonSecurities } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/hooks/useGetPersonSecurities.ts';
import { useGetDealData } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/hooks/useGetDealData.ts';
import { StyledSettingsContainer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { SecurityDeleteConfirmationDrawer } from '@dealstack/frontend/shared/components/SecurityDeleteConfirmationDrawer/SecurityDeleteConfirmationDrawer.tsx';
import { useMipProgrammeEntities } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/hooks/useMipProgrammeEntities';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import styled from 'styled-components';

export const Securities = () => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const tDefault = useTranslationDefault();
	const { mipId, entityId: routeEntityId } = useParams();
	const { data: deal } = useGetDealData();
	const mipData = deal?.payload?.mips?.find(({ id }) => id === mipId);
	const securityValuationDate = mipData?.securityValuationDate;
	const { data: programmeEntities = [], isFetched: hasFetchedProgrammeEntities } =
		useMipProgrammeEntities(mipId);
	const selectedProgrammeEntity =
		programmeEntities.find((entity) => entity.entityId === routeEntityId) ??
		programmeEntities[0];

	const { data: programmeSetting } = useProgrammeSetting(mipId ?? '');

	const shareWithInvestors = programmeSetting?.shareWithInvestorSecurityValuation;

	const selectedMipId = mipData?.id;
	const issuerPersonId =
		routeEntityId ??
		selectedProgrammeEntity?.entityId ??
		(hasFetchedProgrammeEntities ? mipData?.entityId : undefined);
	const issuerEntityName =
		selectedProgrammeEntity?.displayName ?? mipData?.entity?.displayName ?? '';
	const issuerEntityCurrency = selectedProgrammeEntity?.entityCurrency ?? mipData?.currency;
	const [valuationDate, setValuationDate] = useState<Date | null>(
		securityValuationDate ? new Date(securityValuationDate) : null
	);
	const [showSecurityDrawer, setShowSecurityDrawer] = useState(false);
	const [showCreateSecurityDrawer, setShowCreateSecurityDrawer] = useState(false);
	const [showValuationDrawer, setShowValuationDrawer] = useState(false);
	const [securityToEdit, setSecurityToEdit] = useState<GetPersonSecurityDataRes>();
	const [confirmDeleteOpened, setConfirmDeleteOpened] = useState(false);
	const [securityToDelete, setSecurityToDelete] = useState<{ id: string; name: string } | null>(
		null
	);
	const queryClient = useQueryClient();

	// Editing is a programme concern: PUT /security takes a programme editor through the mip-id
	// header, so that is the role this page gates on.
	const { hasPermission: hasProgrammeEditorPermission } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: defaultToEmptyString(mipId),
		permissionRole: PermissionRole.EDITOR,
	});
	// Deleting is not: DELETE soft-delete-one accepts only a deal admin, so gating it on the
	// programme role would show a button the API refuses.
	const { hasPermission: hasDealAdminPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(deal?.payload?.id),
		permissionRole: PermissionRole.ADMIN,
	});

	useEffect(() => {
		if (securityValuationDate) {
			setValuationDate(new Date(securityValuationDate));
		} else {
			setValuationDate(null);
		}
	}, [securityValuationDate]);

	useEffect(() => {
		setSecurityToEdit(undefined);
		setShowSecurityDrawer(false);
		setShowCreateSecurityDrawer(false);
	}, [issuerPersonId]);

	const invalidateQueries = useCallback(async () => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: [
					formatUnicorn(API_PATHS.SECURITY_PERSON_SECURITIES_PERSONID_POST, {
						personId: issuerPersonId || '',
					}),
				],
			}),
		]);
	}, [queryClient, issuerPersonId]);

	const { mutate: createSecurity, isPending: isCreateSecurityLoading } = useCreateSecurity({
		issuerPersonId,
		mipId: selectedMipId || '',
		onSuccess: invalidateQueries,
		currency: issuerEntityCurrency,
	});

	const { mutate: updateSecurity } = useUpdateSecurity({
		mipId: selectedMipId || '',
		onSuccess: invalidateQueries,
	});

	const {
		mutate: deleteSecurity,
		isPending: isDeleteSecurityLoading,
		isSuccess: isDeleteSecuritySuccess,
		reset: resetDeleteSecurity,
	} = useDeleteSecurity({
		onSuccess: invalidateQueries,
	});

	const { mutate: updateMipValuationDate, isPending: isUpdateMipValuationDateLoading } =
		useMutation({
			mutationFn: (variables: Parameters<typeof updateMipRequest>[0]) =>
				updateMipRequest(variables),
		});

	const {
		mutate: updateMipWorkflowSettingsMutation,
		isPending: isUpdateMipWorkflowSettingsLoading,
	} = useMutation({
		mutationFn: (variables: Parameters<typeof updateProgrammeSetting>[0]) =>
			updateProgrammeSetting(variables),
	});

	const { data: securities, isFetching: isFetchingSecurities } = useGetPersonSecurities(
		issuerPersonId,
		false
	);

	const handleSaveClicked = (
		valuationDate: Date | null,
		shareWithInvestorSecurityValuation: boolean
	) => {
		// TODO: We should make updating the valuation date and share with investors a single API call
		updateMipValuationDate(
			{
				securityValuationDate: valuationDate?.toISOString() ?? null,
			},
			{
				onSuccess: () => {
					// This is not ideal but we only want to show the toast after both are successful
					// so we need to wait for both to complete. We should make this a single API call instead.
					updateMipWorkflowSettingsMutation(
						{
							shareWithInvestorSecurityValuation: valuationDate
								? shareWithInvestorSecurityValuation
								: false,
						},
						{
							onSuccess: async () => {
								await queryClient.invalidateQueries({
									queryKey: [API_PATHS.DEAL_DATA_GET],
								});

								showToastV2({
									title: tAdminDashboard(
										'programme_settings.securities.toast.success'
									),
									state: 'success',
								});
								setValuationDate(valuationDate);
								setShowValuationDrawer(false);
							},
							onError: () => {
								showToastV2({
									title: tDefault('settings_updated_failed'),
									state: 'error',
								});
							},
						}
					);
				},
				onError: () => {
					showToastV2({
						title: tDefault('settings_updated_failed'),
						state: 'error',
					});
				},
			}
		);
	};

	const isLoading =
		(!issuerPersonId && !hasFetchedProgrammeEntities) ||
		(!securities && isFetchingSecurities) ||
		isUpdateMipValuationDateLoading ||
		isUpdateMipWorkflowSettingsLoading;

	const securityItems = useMemo(
		() =>
			securities?.securities.map((security) => ({
				id: security.id,
				name: security.name,
				type: security.type,
				isSubjectToVesting: security.isSubjectToVesting,
				fairMarketValuePerUnit: security.fairMarketValuePerUnit,
				currency: security.currency,
				createdAt: security.createdAt,
			})),
		[securities?.securities]
	);

	const aSecurityHasAValuePerUnit = securities?.securities.some(
		(security) => security.fairMarketValuePerUnit
	);

	const handleAddClicked = () => {
		setShowCreateSecurityDrawer(true);
	};

	const handleValuationSettingsClicked = () => {
		setShowValuationDrawer(true);
	};

	const handleEditClicked = useCallback(
		(security: SecurityFormData) => {
			const originalSecurity = securities?.securities.find((s) => s.id === security.id);
			if (originalSecurity) {
				setSecurityToEdit(originalSecurity);
				setShowSecurityDrawer(true);
			}
		},
		[securities?.securities]
	);

	const resetValuation = () => {
		if (!valuationDate && !shareWithInvestors) {
			return;
		}

		updateMipWorkflowSettingsMutation({
			shareWithInvestorSecurityValuation: false,
		});
		updateMipValuationDate(
			{
				securityValuationDate: null,
			},
			{
				onSuccess: () => {
					setValuationDate(null);
				},
			}
		);
	};

	const handleUpdateSecurities = async (
		updatedSecurities: Array<{
			id: string;
			name: string;
			fairMarketValuePerUnit: number | null;
			currency: string;
		}>
	) => {
		for (const updatedSecurity of updatedSecurities) {
			const originalSecurity = securities?.securities.find(
				(s) => s.id === updatedSecurity.id
			);
			if (originalSecurity) {
				await new Promise<void>((resolve) => {
					updateSecurity(
						{
							id: updatedSecurity.id,
							name: originalSecurity.name,
							type: originalSecurity.type,
							isSubjectToVesting: originalSecurity.isSubjectToVesting,
							fairMarketValuePerUnit: updatedSecurity.fairMarketValuePerUnit,
							skipToast: true,
						},
						{
							onSettled: () => resolve(),
						}
					);
				});
			}
		}
	};

	const handleSecuritySaved = () => {
		void invalidateQueries();

		if (!securityToEdit?.fairMarketValuePerUnit) {
			resetValuation();
		}
	};

	const handleCreateSecurity = (securityData: CreateSecurityFormData, options: SubmitOptions) => {
		createSecurity(
			{
				name: securityData.name,
				type: securityData.type,
				currency: securityData.currency,
				isSubjectToVesting: false,
				fairMarketValuePerUnit: null,
			},
			{
				onSuccess: () => {
					resetValuation();
					options.resetForm();

					if (options.shouldCloseOnSuccess) {
						setShowCreateSecurityDrawer(false);
					}
				},
			}
		);
	};

	const handleRemoveSecurity = (securityId: string) => {
		const security = securities?.securities.find((s) => s.id === securityId);
		setSecurityToDelete({ id: securityId, name: security?.name || '' });
		resetDeleteSecurity();
		setShowSecurityDrawer(false);
		setConfirmDeleteOpened(true);
	};

	const handleConfirmDelete = () => {
		if (securityToDelete?.id) {
			deleteSecurity(securityToDelete.id);
		}
	};

	const handleToggleVesting = useCallback(
		(security: SecurityFormData) => {
			if (security.id) {
				updateSecurity({
					id: security.id,
					name: security.name,
					type: security.type,
					isSubjectToVesting: !security.isSubjectToVesting,
					fairMarketValuePerUnit: security.fairMarketValuePerUnit,
				});
			}
		},
		[updateSecurity]
	);

	const securitiesForValuation =
		securities?.securities.map((security) => ({
			id: security.id,
			name: security.name,
			fairMarketValuePerUnit: security.fairMarketValuePerUnit ?? null,
			currency: security.currency,
		})) || [];

	if (isLoading) {
		return <InlineLoader />;
	}

	return (
		<StyledSettingsContainer>
			<Flex direction="column" w="100%">
				{securityItems?.length ? (
					<SecuritiesTable
						securities={securityItems}
						entityName={issuerEntityName}
						onEditClicked={handleEditClicked}
						onAddClicked={handleAddClicked}
						onValuationSettingsClicked={handleValuationSettingsClicked}
						onToggleVesting={handleToggleVesting}
						valuationDate={valuationDate}
						shareWithInvestors={shareWithInvestors}
						style={{ marginBottom: '1rem' }}
					/>
				) : (
					<Flex direction="column" pt="1rem">
						<Text size="lg" fw={600}>
							{issuerEntityName}
						</Text>
						<EmptySecuritiesContainer>
							<EmptySecuritiesIllustration variant="es-box-magnifying-glass" />
							<EmptySecuritiesTitle>
								{issuerEntityName
									? `No securities configured for ${issuerEntityName}`
									: 'No securities configured'}
							</EmptySecuritiesTitle>
							<Button
								variant="primary"
								onClick={handleAddClicked}
								leftSection={<Icon variant="add" size="lg" color="white" />}
							>
								Add first security
							</Button>
						</EmptySecuritiesContainer>
					</Flex>
				)}

				{securityItems?.length ? <VestingTerms /> : null}
			</Flex>

			<EditSecurityDrawer
				opened={showSecurityDrawer}
				onClose={() => {
					setSecurityToEdit(undefined);
					setShowSecurityDrawer(false);
				}}
				security={securityToEdit}
				dealId={deal?.payload?.id || ''}
				onSaved={handleSecuritySaved}
				canEdit={hasProgrammeEditorPermission}
				canDelete={hasDealAdminPermission}
				onDelete={handleRemoveSecurity}
			/>

			<CreateSecurityDrawer
				opened={showCreateSecurityDrawer}
				onClose={() => setShowCreateSecurityDrawer(false)}
				onCreate={handleCreateSecurity}
				isLoading={isCreateSecurityLoading}
				defaultCurrency={issuerEntityCurrency ?? CurrencyType.EUR}
				defaultType={SecurityType.ORDINARY_SHARE}
				lockCurrency
			/>

			<SecurityDeleteConfirmationDrawer
				opened={confirmDeleteOpened}
				onClose={() => setConfirmDeleteOpened(false)}
				securityName={securityToDelete?.name}
				isSuccess={isDeleteSecuritySuccess}
				isLoading={isDeleteSecurityLoading}
				onConfirm={handleConfirmDelete}
			/>

			<ValuationSettingsDrawer
				opened={showValuationDrawer}
				onClose={() => setShowValuationDrawer(false)}
				valuationDate={valuationDate}
				securityValuationDate={
					securityValuationDate ? new Date(securityValuationDate) : null
				}
				shouldShareWithInvestors={shareWithInvestors}
				aSecurityHasAValuePerUnit={!!aSecurityHasAValuePerUnit}
				securities={securitiesForValuation}
				onSave={handleSaveClicked}
				onUpdateSecurities={handleUpdateSecurities}
				isLoading={isUpdateMipValuationDateLoading || isUpdateMipWorkflowSettingsLoading}
			/>
		</StyledSettingsContainer>
	);
};

const EmptySecuritiesContainer = styled(Flex).attrs({
	direction: 'column',
	align: 'center',
	justify: 'center',
})`
	min-height: 264px;
	margin-top: 0.75rem;
	padding: 48px;
	gap: 16px;
	border: 1px solid #d3d8da;
	border-radius: 4px;
`;

const EmptySecuritiesIllustration = styled(Icon)`
	&.svg {
		width: 95px;
		height: 80px;
	}
`;

const EmptySecuritiesTitle = styled(Text)`
	color: #0e171c;
	font-size: 18px;
	font-weight: 400;
	line-height: 24px;
	text-align: center;
`;
